import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Avatar,
  CssBaseline,
} from '@mui/material';
import { LockOutlined as LockOutlinedIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const LoginPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  
  const { login, isAuthenticated } = useAuth();
  const location = useLocation();
  
  const from = location.state?.from?.pathname || '/';

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login({ 
        username, 
        password,
        totp_code: totpCode || undefined
      });
      
      // Check if 2FA is required
      if (result?.requires_2fa) {
        setRequires2FA(true);
        setError('');
      }
    } catch (err: any) {
      if (err.response?.status === 401 && err.response?.data?.detail === 'Incorrect username or password') {
        setError('Invalid username or password');
      } else if (err.response?.data?.requires_2fa) {
        setRequires2FA(true);
        setError('');
      } else {
        setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h4" gutterBottom align="center">
          Sonic Operations Manager
        </Typography>
        <Typography component="h2" variant="h5" gutterBottom>
          Administrator Sign In
        </Typography>
        <Paper elevation={3} sx={{ padding: 4, width: '100%', mt: 3 }}>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              autoFocus={!requires2FA}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading || requires2FA}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading || requires2FA}
            />
            {requires2FA && (
              <TextField
                margin="normal"
                required
                fullWidth
                name="totpCode"
                label="Authenticator Code"
                id="totpCode"
                autoComplete="one-time-code"
                autoFocus
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                disabled={loading}
                helperText="Enter the 6-digit code from your authenticator app"
                inputProps={{ maxLength: 6 }}
              />
            )}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
            {requires2FA && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Two-factor authentication is required. Please enter your 6-digit code.
              </Alert>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading || !username || !password || (requires2FA && totpCode.length !== 6)}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
            {requires2FA && (
              <Button
                fullWidth
                variant="text"
                onClick={() => {
                  setRequires2FA(false);
                  setTotpCode('');
                  setError('');
                }}
                disabled={loading}
              >
                Back to Username/Password
              </Button>
            )}
          </Box>
        </Paper>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} Sonic Internet & Voice Pty Ltd
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default LoginPage;
